import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, ArrowLeft, Folder, CaretUp, CaretDown, Funnel } from "phosphor-react";
import { Button } from "@/components/ui/button";
import { SyncedScrollTable } from "@/components/ui/synced-scroll-table";
import GeniLoader from "../components/GeniLoader";

export default function SetupTrades() {
  const { setupId } = useParams();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [allSetupsData, setAllSetupsData] = useState([]); // For badge comparison
  
  // Filter and sort state
  const [directionFilter, setDirectionFilter] = useState(null); // null | 'long' | 'short'
  const [statusFilter, setStatusFilter] = useState(null); // null | 'win' | 'loss' | 'canceled' | 'open'
  const [sortColumn, setSortColumn] = useState(null); // null | 'rr' | 'profit' | 'date'
  const [sortDirection, setSortDirection] = useState(null); // null | 'asc' | 'desc'

  useEffect(() => {
    fetchData();
  }, [setupId]);

  const fetchData = async () => {
    try {
      // Fetch setup details and all trades in parallel
      const [setupsRes, tradesRes] = await Promise.all([
        api.get('/setups'),
        api.get('/trades')
      ]);

      // Find the setup
      const foundSetup = setupsRes.data.find(s => s.id === setupId);
      setSetup(foundSetup);

      // Filter trades by setup
      const filteredTrades = tradesRes.data.filter(t => t.setup_id === setupId);
      setTrades(filteredTrades);
      
      // Calculate analytics for ALL setups (for badge comparison)
      const allTrades = tradesRes.data;
      const setupAnalyticsMap = setupsRes.data.map(s => {
        const setupTrades = allTrades.filter(t => t.setup_id === s.id);
        const closedTrades = setupTrades.filter(t => t.status === 'closed');
        const wins = closedTrades.filter(t => t.outcome === 'win');
        const losses = closedTrades.filter(t => t.outcome === 'loss');
        
        // Win Rate
        const winRate = closedTrades.length > 0 
          ? Math.round((wins.length / closedTrades.length) * 100) 
          : 0;
        
        // Profit Factor
        const totalWinRR = wins.reduce((sum, t) => sum + (t.rr || 0), 0);
        const totalLossRR = losses.reduce((sum, t) => sum + Math.abs(t.rr || 0), 0);
        const profitFactor = totalLossRR > 0 
          ? totalWinRR / totalLossRR
          : (totalWinRR > 0 ? Infinity : 0);
        
        return {
          id: s.id,
          name: s.name,
          totalTrades: setupTrades.length,
          winRate,
          profitFactor
        };
      });
      
      setAllSetupsData(setupAnalyticsMap);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load setup trades");
    } finally {
      setLoading(false);
    }
  };

  // Cycle direction filter: null → long → short → null
  const cycleDirectionFilter = () => {
    if (directionFilter === null) setDirectionFilter('long');
    else if (directionFilter === 'long') setDirectionFilter('short');
    else setDirectionFilter(null);
  };

  // Cycle status filter: null → win → loss → canceled → open → null
  const cycleStatusFilter = () => {
    if (statusFilter === null) setStatusFilter('win');
    else if (statusFilter === 'win') setStatusFilter('loss');
    else if (statusFilter === 'loss') setStatusFilter('canceled');
    else if (statusFilter === 'canceled') setStatusFilter('open');
    else setStatusFilter(null);
  };

  // Cycle RR sort: null → desc → asc → null
  const cycleRRSort = () => {
    if (sortColumn !== 'rr') {
      setSortColumn('rr');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle Profit sort: null → desc → asc → null
  const cycleProfitSort = () => {
    if (sortColumn !== 'profit') {
      setSortColumn('profit');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle Date sort: null → desc (recent) → asc (oldest) → null
  const cycleDateSort = () => {
    if (sortColumn !== 'date') {
      setSortColumn('date');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Apply filters and sorting
  const filteredAndSortedTrades = useMemo(() => {
    let result = [...trades];

    // Apply direction filter
    if (directionFilter) {
      result = result.filter(t => t.direction === directionFilter);
    }

    // Apply status filter
    if (statusFilter) {
      if (statusFilter === 'open') {
        result = result.filter(t => t.status === 'open');
      } else {
        result = result.filter(t => t.outcome === statusFilter);
      }
    }

    // Apply sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'rr') {
          aVal = a.rr ?? 0;
          bVal = b.rr ?? 0;
        } else if (sortColumn === 'profit') {
          aVal = a.profit_usd ?? 0;
          bVal = b.profit_usd ?? 0;
        } else if (sortColumn === 'date') {
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
        }
        
        if (sortDirection === 'desc') {
          return bVal - aVal;
        } else {
          return aVal - bVal;
        }
      });
    } else {
      // Default sort by date descending
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return result;
  }, [trades, directionFilter, statusFilter, sortColumn, sortDirection]);

  // Calculate setup analytics (matching Analytics page format)
  const setupAnalytics = useMemo(() => {
    const closedTrades = trades.filter(t => t.status === 'closed');
    const wins = closedTrades.filter(t => t.outcome === 'win');
    const losses = closedTrades.filter(t => t.outcome === 'loss');
    
    const totalTrades = trades.length;
    const closedCount = closedTrades.length;
    
    // Win Rate
    const winRate = closedCount > 0 
      ? Math.round((wins.length / closedCount) * 100) 
      : 0;
    
    // Avg Win RR
    const winRRs = wins.map(t => t.rr).filter(rr => rr !== null && rr !== undefined);
    const avgWinRR = winRRs.length > 0 
      ? (winRRs.reduce((a, b) => a + b, 0) / winRRs.length).toFixed(2)
      : null;
    
    // Avg RR (all closed trades)
    const allRRs = closedTrades.map(t => t.rr).filter(rr => rr !== null && rr !== undefined);
    const avgRR = allRRs.length > 0 
      ? (allRRs.reduce((a, b) => a + b, 0) / allRRs.length).toFixed(2)
      : '0.00';

    // Avg Loss RR (only losing trades)
    const losingTrades = closedTrades.filter(t => t.outcome === 'loss');
    const losingRRs = losingTrades.map(t => t.rr).filter(rr => rr !== null && rr !== undefined);
    const avgLossRR = losingRRs.length > 0
      ? (losingRRs.reduce((a, b) => a + b, 0) / losingRRs.length).toFixed(2)
      : null;
    
    // Profit Factor
    const totalWinRR = winRRs.reduce((a, b) => a + b, 0);
    const lossRRs = losses.map(t => Math.abs(t.rr || 0)).filter(rr => rr > 0);
    const totalLossRR = lossRRs.reduce((a, b) => a + b, 0);
    const profitFactor = totalLossRR > 0 
      ? (totalWinRR / totalLossRR).toFixed(2)
      : (totalWinRR > 0 ? '∞' : null);
    
    return {
      totalTrades,
      winRate,
      avgWinRR,
      avgRR,
      profitFactor
    };
  }, [trades]);

  // Helper for Profit Factor color
  const getProfitFactorColor = (pf) => {
    if (pf === null || pf === '∞') return 'text-white';
    const value = parseFloat(pf);
    if (value >= 2) return 'text-[#00E676]';
    if (value >= 1.5) return 'text-[#00E676]/80';
    if (value >= 1) return 'text-yellow-400';
    return 'text-[#FF3B30]';
  };

  // Calculate badges for this setup based on comparison with all setups
  const setupBadges = useMemo(() => {
    const MIN_TRADES = 5;
    const badges = [];
    const currentSetupData = allSetupsData.find(s => s.id === setupId);
    
    if (!currentSetupData || allSetupsData.length === 0) return badges;
    
    // Filter qualified setups (min 5 trades, valid profit factor)
    const qualifiedSetups = allSetupsData.filter(s => 
      s.totalTrades >= MIN_TRADES && s.profitFactor !== Infinity
    );
    
    // MOST USED: highest number of trades
    const mostUsed = [...allSetupsData]
      .filter(s => s.totalTrades > 0)
      .sort((a, b) => b.totalTrades - a.totalTrades)[0];
    if (mostUsed && mostUsed.id === setupId) {
      badges.push({ label: 'Most Used', color: 'bg-blue-500/10 border-blue-500/30 text-blue-400' });
    }
    
    // TOP SETUP: highest profit factor (min 5 trades)
    if (qualifiedSetups.length > 0) {
      const topSetup = [...qualifiedSetups].sort((a, b) => b.profitFactor - a.profitFactor)[0];
      if (topSetup && topSetup.id === setupId && topSetup.profitFactor >= 1.5) {
        badges.push({ label: 'Top Setup', color: 'bg-[#00E676]/10 border-[#00E676]/30 text-[#00E676]' });
      }
    }
    
    // NEEDS REVIEW: lowest profit factor (min 5 trades, PF < 1.0)
    if (qualifiedSetups.length > 0) {
      const needsReview = [...qualifiedSetups].sort((a, b) => a.profitFactor - b.profitFactor)[0];
      if (needsReview && needsReview.id === setupId && needsReview.profitFactor < 1.0) {
        badges.push({ label: 'Needs Review', color: 'bg-orange-500/10 border-orange-500/30 text-orange-400' });
      }
    }
    
    // CONSISTENT: win rate >= 60% (min 5 trades)
    if (currentSetupData.totalTrades >= MIN_TRADES && currentSetupData.winRate >= 60) {
      badges.push({ label: 'Consistent', color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' });
    }
    
    return badges;
  }, [allSetupsData, setupId]);

  // Column header component with filter/sort indicator
  const ColumnHeader = ({ label, isActive, activeLabel, onClick, sortDir }) => (
    <th 
      className={`p-3 cursor-pointer select-none transition-colors hover:bg-white/5 ${
        isActive ? 'text-white' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {isActive && (
          <span className="flex items-center">
            {sortDir === 'desc' && <CaretDown size={12} weight="bold" className="text-[#00E676]" />}
            {sortDir === 'asc' && <CaretUp size={12} weight="bold" className="text-[#00E676]" />}
            {!sortDir && activeLabel && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-[#00E676]/20 text-[#00E676] font-semibold ml-1">
                {activeLabel}
              </span>
            )}
          </span>
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="setup-trades-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            onClick={() => navigate('/analytics')}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/5 p-2"
            data-testid="back-to-analytics"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Folder size={28} weight="duotone" className="text-white/60" />
              <div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">SETUP</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="setup-title">
                    {setup?.name || 'Unknown Setup'}
                  </h2>
                  {/* Dynamic Badges */}
                  {setupBadges.length > 0 && (
                    <div className="flex items-center gap-2">
                      {setupBadges.map((badge, idx) => (
                        <span 
                          key={idx}
                          className={`text-[10px] px-2 py-1 rounded border font-mono uppercase tracking-wider ${badge.color}`}
                          data-testid={`badge-${badge.label.toLowerCase().replace(' ', '-')}`}
                        >
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {setup?.description && (
              <p className="text-white/60 mt-2 ml-10">{setup.description}</p>
            )}
          </div>
        </div>

        {/* Performance Summary Card */}
        {trades.length > 0 && (
          <div className="mb-6" data-testid="setup-performance-summary">
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
              Performance Summary
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">TRADES</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-trades">
                    {setupAnalytics.totalTrades}
                  </div>
                </div>
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">WIN RATE</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-winrate">
                    {setupAnalytics.winRate}%
                  </div>
                </div>
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">AVG WIN RR</div>
                  <div className="text-xl font-bold font-mono text-[#00E676]" data-testid="summary-avg-win-rr">
                    {setupAnalytics.avgWinRR !== null ? setupAnalytics.avgWinRR : 'N/A'}
                  </div>
                </div>
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">AVG LOSS RR</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-avg-loss-rr">
                    {avgLossRR !== null ? `${avgLossRR}R` : '—'}
                  </div>
                </div>
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">PROFIT FACTOR</div>
                  <div 
                    className={`text-xl font-bold font-mono ${getProfitFactorColor(setupAnalytics.profitFactor)}`}
                    data-testid="summary-pf"
                  >
                    {setupAnalytics.profitFactor !== null ? setupAnalytics.profitFactor : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {trades.length === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Folder size={32} weight="duotone" className="text-white/40" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">No Trades Found</h3>
            <p className="text-white/60">
              No trades have been recorded with this setup yet.
            </p>
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trade List */}
            <div className="flex-1">
              {/* Active Filters Summary */}
              {(directionFilter || statusFilter || sortColumn) && (
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <Funnel size={14} className="text-white/40" />
                  <span className="text-white/40">Active:</span>
                  {directionFilter && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {directionFilter.toUpperCase()}
                    </span>
                  )}
                  {statusFilter && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {statusFilter.toUpperCase()}
                    </span>
                  )}
                  {sortColumn && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {sortColumn.toUpperCase()} {sortDirection === 'desc' ? '↓' : '↑'}
                    </span>
                  )}
                  <button 
                    onClick={() => {
                      setDirectionFilter(null);
                      setStatusFilter(null);
                      setSortColumn(null);
                      setSortDirection(null);
                    }}
                    className="text-white/40 hover:text-white ml-2 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
              
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                <SyncedScrollTable maxHeight="calc(100vh - 350px)">
                  <table className="w-full" data-testid="trades-table">
                    <thead className="bg-[#0A0A0A]">
                      <tr className="text-left text-xs text-white/40 font-mono">
                        <th className="p-3">ASSET</th>
                        <ColumnHeader 
                          label="DIRECTION" 
                          isActive={!!directionFilter}
                          activeLabel={directionFilter?.toUpperCase()}
                          onClick={cycleDirectionFilter}
                        />
                        <ColumnHeader 
                          label="STATUS" 
                          isActive={!!statusFilter}
                          activeLabel={statusFilter?.toUpperCase()}
                          onClick={cycleStatusFilter}
                        />
                        <ColumnHeader 
                          label="RR" 
                          isActive={sortColumn === 'rr'}
                          sortDir={sortColumn === 'rr' ? sortDirection : null}
                          onClick={cycleRRSort}
                        />
                        <ColumnHeader 
                          label="PROFIT" 
                          isActive={sortColumn === 'profit'}
                          sortDir={sortColumn === 'profit' ? sortDirection : null}
                          onClick={cycleProfitSort}
                        />
                        <ColumnHeader 
                          label="DATE" 
                          isActive={sortColumn === 'date'}
                          sortDir={sortColumn === 'date' ? sortDirection : null}
                          onClick={cycleDateSort}
                        />
                      </tr>
                    </thead>
                  <tbody>
                    {filteredAndSortedTrades.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-white/40">
                          No trades match current filters
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedTrades.map((trade) => (
                        <tr
                          key={trade.id}
                          data-testid={`trade-row-${trade.id}`}
                          onClick={() => setSelectedTrade(trade)}
                          className={`trade-row border-t border-white/10 cursor-pointer hover:bg-white/5 ${
                            selectedTrade?.id === trade.id ? "bg-white/5" : ""
                          }`}
                        >
                          <td className="p-3">
                            <div className="font-mono text-white font-bold">{trade.asset}</div>
                            <div className="text-xs text-white/40 uppercase">{trade.trade_status}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {trade.direction === "long" ? (
                                <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                              ) : (
                                <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                              )}
                              <span
                                className={`font-mono text-sm uppercase ${
                                  trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                                }`}
                              >
                                {trade.direction}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                              trade.status === "open" 
                                ? "bg-[#FFC107]/10 text-[#FFC107]" 
                                : trade.outcome === "win"
                                ? "bg-[#00E676]/10 text-[#00E676]"
                                : trade.outcome === "loss"
                                ? "bg-[#FF3B30]/10 text-[#FF3B30]"
                                : trade.outcome === "canceled"
                                ? "bg-white/10 text-white/40"
                                : "bg-white/10 text-white/60"
                            }`}>
                              {trade.status === "open" ? "OPEN" : trade.outcome?.toUpperCase() || "CLOSED"}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="font-mono text-white">
                              {trade.rr?.toFixed(2) || '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            {trade.profit_usd !== null && trade.profit_usd !== undefined ? (
                              <div className="flex items-center gap-1.5">
                                <span 
                                  className={`font-mono font-bold ${
                                    trade.profit_usd > 0 ? 'text-[#00E676]' : 
                                    trade.profit_usd < 0 ? 'text-[#FF3B30]' : 
                                    'text-white/60'
                                  }`}
                                >
                                  {trade.profit_usd > 0 ? '+' : ''}${trade.profit_usd.toFixed(2)}
                                </span>
                                {trade.profit_adjusted_manually && (
                                  <span 
                                    className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                                      trade.profit_adjustment_reason === 'partial_profit' 
                                        ? 'bg-[#00E676]/15 text-[#00E676]/80' 
                                        : 'bg-[#FFC107]/15 text-[#FFC107]/80'
                                    }`}
                                  >
                                    {trade.profit_adjustment_reason === 'partial_profit' ? 'Partial' : 'Reduced'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-white/40 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                </SyncedScrollTable>
                
                {/* Results count */}
                {(directionFilter || statusFilter) && (
                  <div className="p-3 border-t border-white/10 text-xs text-white/40">
                    Showing {filteredAndSortedTrades.length} of {trades.length} trades
                  </div>
                )}
              </div>
            </div>

            {/* Trade Details Panel - Read Only */}
            {selectedTrade && (
              <div className="w-[400px] shrink-0">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sticky top-8" data-testid="trade-details-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold font-heading">Trade Details</h3>
                    <Button
                      onClick={() => setSelectedTrade(null)}
                      variant="ghost"
                      size="sm"
                      className="text-white/40 hover:text-white hover:bg-white/5"
                    >
                      <ArrowLeft size={16} />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Chart Image */}
                    {selectedTrade.image_data && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm overflow-hidden">
                        <img 
                          src={`data:image/png;base64,${selectedTrade.image_data}`}
                          alt="Trade chart"
                          className="w-full h-auto object-contain"
                          data-testid="trade-chart-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Asset & Direction */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black font-heading">{selectedTrade.asset}</div>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-bold ${
                          selectedTrade.direction === "long" 
                            ? "bg-[#00E676]/10 text-[#00E676]" 
                            : "bg-[#FF3B30]/10 text-[#FF3B30]"
                        }`}>
                          {selectedTrade.direction === "long" ? (
                            <TrendUp size={14} weight="bold" />
                          ) : (
                            <TrendDown size={14} weight="bold" />
                          )}
                          {selectedTrade.direction.toUpperCase()}
                        </div>
                      </div>
                      <div className={`text-2xl font-bold font-mono ${
                        selectedTrade.outcome === "win" ? "text-[#00E676]" :
                        selectedTrade.outcome === "loss" ? "text-[#FF3B30]" :
                        selectedTrade.outcome === "canceled" ? "text-white/40" :
                        "text-[#FFC107]"
                      }`}>
                        {selectedTrade.status === "open" ? "OPEN" : selectedTrade.outcome?.toUpperCase() || "CLOSED"}
                      </div>
                    </div>

                    {/* Price Levels */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs font-mono mb-1">ENTRY</div>
                        <div className="text-white font-mono font-semibold">
                          ${selectedTrade.entry?.toLocaleString()}
                        </div>
                      </div>
                      <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs font-mono mb-1">STOP LOSS</div>
                        <div className="text-[#FF3B30] font-mono font-semibold">
                          ${selectedTrade.stop_loss?.toLocaleString()}
                        </div>
                      </div>
                      {selectedTrade.take_profit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">TAKE PROFIT</div>
                          <div className="text-[#00E676] font-mono font-semibold">
                            ${selectedTrade.take_profit?.toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs font-mono mb-1">RR</div>
                        <div className="text-white font-mono text-xl font-bold">
                          {selectedTrade.rr?.toFixed(2) || '-'}
                        </div>
                      </div>
                      <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                        <div className="text-white/40 text-xs font-mono mb-1">HAND SIZE</div>
                        <div className="text-white font-mono text-xl font-bold">
                          ${selectedTrade.hand_size?.toLocaleString() || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Profit Section - Read Only */}
                    {selectedTrade.status === 'closed' && selectedTrade.profit_usd !== null && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm p-4">
                        <div className="text-white/40 text-xs font-mono mb-2">PROFIT (USD)</div>
                        <div className={`font-mono text-2xl font-bold ${
                          selectedTrade.profit_usd > 0 ? 'text-[#00E676]' : 
                          selectedTrade.profit_usd < 0 ? 'text-[#FF3B30]' : 'text-white'
                        }`}>
                          {selectedTrade.profit_usd > 0 ? '+' : ''}${selectedTrade.profit_usd.toFixed(2)}
                        </div>
                        {selectedTrade.profit_adjusted_manually && (
                          <div className="mt-2 text-xs text-white/40 flex items-center gap-1">
                            <span className="bg-white/10 px-2 py-0.5 rounded-sm">
                              {selectedTrade.profit_adjustment_reason === 'partial_profit' ? 'Partial Profit' : 'Reduced Loss'}
                            </span>
                            <span>· Manually adjusted</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Additional Info */}
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      {selectedTrade.timeframe && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Timeframe</span>
                          <span className="text-white font-mono">{selectedTrade.timeframe}</span>
                        </div>
                      )}
                      {selectedTrade.confidence && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Confidence</span>
                          <span className={`font-mono uppercase ${
                            selectedTrade.confidence === "high" ? "text-[#00E676]" :
                            selectedTrade.confidence === "medium" ? "text-[#FFC107]" :
                            "text-[#FF3B30]"
                          }`}>
                            {selectedTrade.confidence}
                          </span>
                        </div>
                      )}
                      {selectedTrade.closing_price && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Closing Price</span>
                          <span className="text-white font-mono">${selectedTrade.closing_price?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Created</span>
                        <span className="text-white/60">
                          {selectedTrade.created_at && format(new Date(selectedTrade.created_at), "MMM d, yyyy HH:mm")}
                        </span>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedTrade.notes && (
                      <div className="pt-4 border-t border-white/10">
                        <div className="text-white/40 text-xs font-mono mb-2">NOTES</div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                      </div>
                    )}

                    {/* Read-only notice */}
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-white/30 text-xs text-center">
                        This is a read-only view. To edit trades, go to Trade History.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
